import axios from "axios";
import { dateConvertinterceptor, onRequestFulfilled } from "./dateConvertInterceptor";

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? "https://api.corgisoftware.pl",
});

api.interceptors.request.use(onRequestFulfilled);
api.interceptors.response.use(dateConvertinterceptor);

const endpoints = {
  ADMIN_AUTH: {
    ME: "/admin/auth/me",
    LOGIN: "/admin/auth/login",
  },
  DICTIONARIES: {
    GET: (skip: number, take: number, searchPhrase: string, filter: string = "", orderBy: string = "") =>
      `/admin/dictionaries?${queryParamsToText([
        ...basePagingToQueryParams(skip, take, searchPhrase),
        { name: "filter", value: filter },
        { name: "orderBy", value: orderBy },
      ])}`,
    GET_BY_ID: (dictionaryId: number) => `/admin/dictionaries/${dictionaryId}`,
    GET_VALUE: (dictionaryValueId: number) => `/admin/dictionary-values/${dictionaryValueId}`,
    DELETE: (dictionaryId: number) => `/admin/dictionaries/${dictionaryId}`,
    DELETE_VALUE: (dictionaryId: number, id: number) => `/admin/dictionaries/${dictionaryId}/values/${id}`,
    CREATE: () => `/admin/dictionaries`,
    CREATE_VALUE: (dictionaryId: number) => `/admin/dictionaries/${dictionaryId}/values`,
    UPDATE_VALUE: (dictionaryId: number, id: number) => `/admin/dictionaries/${dictionaryId}/values/${id}`,
  },
  ADMIN_USERS: {
    GET: (skip: number, take: number, filter?: string, orderBy?: string) =>
      `/admin/users?${queryParamsToText([
        ...basePagingToQueryParams(skip, take, ""),
        { name: "filter", value: filter },
        { name: "orderBy", value: orderBy },
      ])}`,
    GET_BY_ID: (userId: number) => `/admin/users/${userId}`,
    CREATE: `/admin/users`,
    GET_ADMINS: () => `/admin/users/admins`,
  },
  TRAINERS: {
    GET: (skip: number, take: number, searchPhrase: string, filter: string, orderBy?: string) =>
      `/admin/trainers?${queryParamsToText([
        ...basePagingToQueryParams(skip, take, searchPhrase),
        { name: "filter", value: filter },
        { name: "orderBy", value: orderBy },
      ])}`,
    GET_BY_ID: (trainerId: number) => `/admin/trainers/${trainerId}`,
    UPDATE: (trainerId: number) => `/admin/trainers/${trainerId}`,
    DELETE: (trainerId: number) => `/admin/trainers/${trainerId}`,
    CREATE: () => `/admin/trainers/`,
    GET_FILES: (trainerId: number) => `/admin/trainers/${trainerId}/files`,
    CREATE_FILE: (trainerId: number) => `/admin/trainers/${trainerId}/files`,
    SET_FILE_STATUS: (trainerId: number, fileId: number) =>
      `/admin/trainers/${trainerId}/files/${fileId}/status`,
    DOWNLOAD_FILE: (trainerId: number, fileId: number) =>
      `/admin/trainers/${trainerId}/files/${fileId}/download`,
    GET_FILE_PREVIEW_URL: (trainerId: number, fileId: number) =>
      `/admin/trainers/${trainerId}/files/${fileId}/preview-url`,
    DELETE_FILE: (trainerId: number, fileId: number) =>
      `/admin/trainers/${trainerId}/files/${fileId}`,
    GET_FILE_COMMENTS: (trainerId: number, fileId: number) =>
      `/admin/trainers/${trainerId}/files/${fileId}/comments`,

    GET_GROUPS: (skip: number, take: number, searchPhrase: string, filter: string, orderBy?: string) =>
      `/admin/trainergroups?${queryParamsToText([
        ...basePagingToQueryParams(skip, take, searchPhrase),
        { name: "filter", value: filter },
        { name: "orderBy", value: orderBy },
      ])}`,
    CREATE_GROUP: () => `/admin/trainergroups/`,
    GET_GROUP_BY_ID: (groupId: number) => `/admin/trainergroups/${groupId}`,
    DELETE_GROUP: (groupId: number) => `/admin/trainergroups/${groupId}`,
    GET_WORK_HISTORY: (trainerId: number) => `/admin/trainers/${trainerId}/work`,
    GET_AVAILABILITY: (trainerId: number) => `/admin/trainers/${trainerId}/availability`,
    GET_AVAILABILITY_REQUESTS: (trainerId: number) => `/admin/trainers/${trainerId}/availability-requests`,
    CREATE_AVAILABILITY_REQUEST: (trainerId: number) => `/admin/trainers/${trainerId}/availability-requests`,
    CANCEL_AVAILABILITY_REQUEST: (trainerId: number, requestId: number) =>
      `/admin/trainers/${trainerId}/availability-requests/${requestId}`,
    SEND_SCHEDULE: (trainerId: number, courseId: number) =>
      `/admin/trainers/${trainerId}/courses/${courseId}/emailschedule`,
    GENERATE_ACCOUNT: (trainerId: number) => `/admin/trainers/${trainerId}/generate-account`,
    SEND_INVITE: (trainerId: number) => `/admin/trainers/${trainerId}/send-invite`,
    UNLINK_ACCOUNT: (trainerId: number) => `/admin/trainers/${trainerId}/unlink-account`,
    BULK_GENERATE_ACCOUNT: () => `/admin/trainers/bulk-generate-account`,
    BULK_SEND_INVITE: () => `/admin/trainers/bulk-send-invite`,
  },
  TRAINER_LECTURE_CONFIRMATIONS: {
    GET: (
      skip: number,
      take: number,
      trainerId?: number,
      courseId?: number,
      dateFrom?: string,
      dateTo?: string,
      status?: string,
    ) =>
      `/admin/trainer-lecture-confirmations?${queryParamsToText([
        { name: "skip", value: skip.toString() },
        { name: "take", value: take.toString() },
        { name: "trainerId", value: trainerId?.toString() },
        { name: "courseId", value: courseId?.toString() },
        { name: "dateFrom", value: dateFrom },
        { name: "dateTo", value: dateTo },
        { name: "status", value: status },
      ])}`,
    SEND_REQUEST: (trainerId: number, courseScheduleId: number) =>
      `/admin/trainer-lecture-confirmations/${trainerId}/${courseScheduleId}/send-request`,
  },
  CATEGORIES: {
    GET: () => `/admin/categories`,
    GET_TREE: () => `/admin/categories/tree`,
    GET_BY_ID: (categoryId: number) => `/admin/categories/${categoryId}`,
    CREATE: () => `/admin/categories`,
    UPDATE: (categoryId: number) => `/admin/categories/${categoryId}`,
  },
  CATEGORIES_TERMS: {
    GET_TERMS: (categoryId: number) => `/admin/categories/${categoryId}/terms`,
    CREATE: (categoryId: number) => `/admin/categories/${categoryId}/terms`,
    UPDATE: (categoryId: number, termId: number) => `/admin/categories/${categoryId}/terms/${termId}`,
    DELETE: (categoryId: number, termId: number) => `/admin/categories/${categoryId}/terms/${termId}`,
  },
  CATEGORY_FORM_GROUPS: {
    GET_ALL: (categoryId: number) => `/admin/categories/${categoryId}/form-groups`,
    ADD_GROUP: (categoryId: number) => `/admin/categories/${categoryId}/form-groups`,
    UPDATE_GROUP: (categoryId: number, groupId: number) => `/admin/categories/${categoryId}/form-groups/${groupId}`,
    DELETE_GROUP: (categoryId: number, groupId: number) => `/admin/categories/${categoryId}/form-groups/${groupId}`,
    ADD_FIELD: (categoryId: number, groupId: number) => `/admin/categories/${categoryId}/form-groups/${groupId}/fields`,
    UPDATE_FIELD: (categoryId: number, groupId: number, fieldId: number) =>
      `/admin/categories/${categoryId}/form-groups/${groupId}/fields/${fieldId}`,
    DELETE_FIELD: (categoryId: number, groupId: number, fieldId: number) =>
      `/admin/categories/${categoryId}/form-groups/${groupId}/fields/${fieldId}`,
  },
  STUDENTS: {
    GET: (skip: number, take: number, searchPhrase: string) =>
      `/admin/students?${queryParamsToText(basePagingToQueryParams(skip, take, searchPhrase))}`,
    GET_BY_ID: (studentId: number) => `/admin/students/${studentId}`,
    UPDATE: (studentId: number) => `/admin/students/${studentId}`,
    CREATE_NOTE: (studentId: number) => `admin/users/${studentId}/notes`,
    GET_NOTES: (studentId: number) => `admin/users/${studentId}/notes`,
    UPDATE_NOTE: (studentId: number, noteId: number) => `admin/users/${studentId}/notes/${noteId}`,
    DELETE_NOTE: (studentId: number, noteId: number) => `admin/users/${studentId}/notes/${noteId}`,
    BLOCK: (studentId: number) => `/admin/students/${studentId}/block`,
    DELETE: (studentId: number) => `/admin/students/${studentId}`,
    CHANGE_EMAIL: (studentId: number) => `/admin/students/${studentId}/email`,
  },
  PAYMENTS: {
    GET: (skip: number, take: number, searchPhrase: string, filter: string, orderBy: string) =>
      `/admin/payments?${queryParamsToText([
        ...basePagingToQueryParams(skip, take, searchPhrase),
        { name: "filter", value: filter },
        { name: "orderBy", value: orderBy },
      ])}`,
    GET_BY_ID: (paymentId: number) => `/admin/payments/${paymentId}`,
    REFUND: (paymentId: number) => `/admin/payments/${paymentId}/refund`,
    RETRY_REFUND: (refundId: number) => `/admin/payments/refunds/${refundId}/retry`,
    REGENERATE_INVOICE: (paymentId: number) => `/admin/payments/${paymentId}/regenerate-invoice`,
    FETCH_INVOICE_CONFIGURATION: (paymentId: number, courseId?: number) =>
      `/admin/payments/${paymentId}/invoice-configuration${courseId ? `?courseId=${courseId}` : ""}`,
    UPDATE_INVOICE_CONFIGURATION: (paymentId: number, courseId?: number) =>
      `/admin/payments/${paymentId}/invoice-configuration${courseId ? `?courseId=${courseId}` : ""}`,
    FETCH_KSEF_XML: (paymentId: number, courseId?: number) =>
      `/admin/payments/${paymentId}/ksef-xml${courseId ? `?courseId=${courseId}` : ""}`,
    FETCH_KSEF_INVOICE_PDF: (paymentId: number, courseId?: number) =>
      `/admin/payments/${paymentId}/ksef-invoice-pdf${courseId ? `?courseId=${courseId}` : ""}`,
    GENERATE_KSEF_XML: (paymentId: number, courseId?: number) =>
      `/admin/payments/${paymentId}/ksef-xml${courseId ? `?courseId=${courseId}` : ""}`,
    UPDATE_KSEF_XML: (paymentId: number, courseId?: number) =>
      `/admin/payments/${paymentId}/ksef-xml${courseId ? `?courseId=${courseId}` : ""}`,
    SUBMIT_TO_KSEF: (paymentId: number, courseId?: number) =>
      `/admin/payments/${paymentId}/submit-to-ksef${courseId ? `?courseId=${courseId}` : ""}`,
    DELAYED: (skip: number, take: number, searchPhrase: string, filter: string, orderBy: string) =>
      `/admin/payments/delayed?${queryParamsToText([
        ...basePagingToQueryParams(skip, take, searchPhrase),
        { name: "filter", value: filter },
        { name: "orderBy", value: orderBy },
      ])}`,
  },
  BLOBS: {
    GET_SAS_URL: (blobName: string, container: number) =>
      `/admin/blobs/get-sas-url?blob=${encodeURIComponent(blobName)}&container=${container}`,
  },
  REPORTS: {
    GET_COURSES_FINANCIAL_REPORT: (filter: string) =>
      `/admin/reports/courses-financial?${queryParamsToText([{ name: "filter", value: filter }])}`,
    GET_COURSE_GROUPS_FINANCIAL_REPORT: (filter: string) =>
      `/admin/reports/course-groups-financial?${queryParamsToText([{ name: "filter", value: filter }])}`,
    GET_SURVEYS_COURSES_REPORT: (filter: string) =>
      `/admin/reports/surveys-courses?${queryParamsToText([{ name: "filter", value: filter }])}`,
    GET_SURVEYS_COURSE_REPORT: (filter: string, courseId: number) =>
      `/admin/reports/surveys-course?${queryParamsToText([
        { name: "filter", value: filter },
        { name: "courseId", value: courseId.toString() },
      ])}`,
    GET_USER_REGISTRATIONS: (filter: string) =>
      `/admin/reports/user-registrations?${queryParamsToText([{ name: "filter", value: filter }])}`,
    GET_COURSE_REGISTRATIONS_BY_COURSE: (filter: string) =>
      `/admin/reports/course-registrations-by-course?${queryParamsToText([{ name: "filter", value: filter }])}`,
    GET_PLACE_COURSES_REPORT: (filter: string) =>
      `/admin/reports/place-courses?${queryParamsToText([{ name: "filter", value: filter }])}`,
  },
  USER_PAYMENTS: {
    GET: (userId: number, courseId: number) => `/admin/users/${userId}/courses/${courseId}/payments`,
    CHANGE_STATUS: (userId: number, courseId: number, courseTrancheId: number) =>
      `/admin/users/${userId}/courses/${courseId}/payments/tranches/${courseTrancheId}/status`,
    CHANGE_PRICE: (userId: number, courseId: number, courseTrancheId: number) =>
      `/admin/users/${userId}/courses/${courseId}/payments/tranches/${courseTrancheId}/price`,
    POSTPONE_PAYMENT: (userId: number, courseId: number, courseTrancheId: number) =>
      `/admin/users/${userId}/courses/${courseId}/payments/tranches/${courseTrancheId}/postpone`,
    CREATE_TRANCHE: (userId: number, courseId: number) =>
      `/admin/users/${userId}/courses/${courseId}/payments/tranches`,
    UPDATE_TRANCHE: (userId: number, courseId: number, courseTrancheId: number) =>
      `/admin/users/${userId}/courses/${courseId}/payments/tranches/${courseTrancheId}`,
    DELETE_TRANCHE: (userId: number, courseId: number, courseTrancheId: number, trancheName: string) =>
      `/admin/users/${userId}/courses/${courseId}/payments/tranches/${courseTrancheId}?trancheName=${encodeURIComponent(trancheName)}`,
  },
  USER_SURVEYS: {
    GET: (skip: number, take: number, searchPhrase: string, filter: string, orderBy: string) =>
      `/admin/user-surveys?${queryParamsToText([
        ...basePagingToQueryParams(skip, take, searchPhrase),
        { name: "filter", value: filter },
        { name: "orderBy", value: orderBy },
      ])}`,
    GET_BY_ID: (userSurveyId: number) => `/admin/user-surveys/${userSurveyId}`,
  },
  TOPICS: {
    GET: (skip: number, take: number, searchPhrase: string, filter: string, orderBy: string) =>
      `/admin/topics?${queryParamsToText([
        ...basePagingToQueryParams(skip, take, searchPhrase),
        { name: "filter", value: filter },
        { name: "orderBy", value: orderBy },
      ])}`,
    GET_BY_ID: (topicId: number) => `/admin/topics/${topicId}`,
    GET_APPEARANCES: (topicId: number) => `/admin/topics/${topicId}/appearances`,
    UPDATE: (topicId: number) => `/admin/topics/${topicId}`,
    CREATE: () => `/admin/topics`,
    DELETE: (topicId: number) => `/admin/topics/${topicId}`,
  },
  TOPIC_GROUPS: {
    GET: (skip: number, take: number, searchPhrase: string, filter: string, orderBy: string) =>
      `/admin/topicgroups?${queryParamsToText([
        ...basePagingToQueryParams(skip, take, searchPhrase),
        { name: "filter", value: filter },
        { name: "orderBy", value: orderBy },
      ])}`,
    GET_APPEARANCES: (topicId: number) => `/admin/topicgroups/${topicId}/appearances`,
    GET_BY_ID: (topicGroupId: number) => `/admin/topicgroups/${topicGroupId}`,
    UPDATE: (topicGroupId: number) => `/admin/topicgroups/${topicGroupId}`,
    CREATE: () => `/admin/topicgroups`,
    DELETE: (topicGroupId: number) => `/admin/topicgroups/${topicGroupId}`,
  },
  EMAILS: {
    GET: (skip: number, take: number, searchPhrase: string, filter: string, orderBy: string) =>
      `/admin/emails?${queryParamsToText([
        ...basePagingToQueryParams(skip, take, searchPhrase),
        { name: "filter", value: filter },
        { name: "orderBy", value: orderBy },
      ])}`,
    GET_BY_ID: (emailId: number) => `/admin/emails/${emailId}`,
    DELETE: (emailId: number) => `/admin/emails/${emailId}`,
  },
  SCHEDULED_EMAILS: {
    GET: (skip: number, take: number, searchPhrase: string, filter: string = "", orderBy: string = "") =>
      `/admin/scheduled-emails?${queryParamsToText([
        ...basePagingToQueryParams(skip, take, searchPhrase),
        { name: "filter", value: filter },
        { name: "orderBy", value: orderBy },
      ])}`,
    GET_BY_ID: (id: number) => `/admin/scheduled-emails/${id}`,
    GET_RECIPIENTS: (id: number) => `/admin/scheduled-emails/${id}/recipients`,
    CREATE: () => `/admin/scheduled-emails`,
    UPDATE: (id: number) => `/admin/scheduled-emails/${id}`,
    SCHEDULE: (id: number) => `/admin/scheduled-emails/${id}/schedule`,
    SEND: (id: number) => `/admin/scheduled-emails/${id}/send`,
    CANCEL: (id: number) => `/admin/scheduled-emails/${id}/cancel`,
    DELETE: (id: number) => `/admin/scheduled-emails/${id}`,
  },
  SCHEDULED_MESSAGES: {
    GET: (skip: number, take: number, searchPhrase: string, filter: string = "", orderBy: string = "") =>
      `/admin/scheduled-messages?${queryParamsToText([
        ...basePagingToQueryParams(skip, take, searchPhrase),
        { name: "filter", value: filter },
        { name: "orderBy", value: orderBy },
      ])}`,
    GET_BY_ID: (id: number) => `/admin/scheduled-messages/${id}`,
    GET_RECIPIENTS: (id: number) => `/admin/scheduled-messages/${id}/recipients`,
    CREATE: () => `/admin/scheduled-messages`,
    UPDATE: (id: number) => `/admin/scheduled-messages/${id}`,
    SCHEDULE: (id: number) => `/admin/scheduled-messages/${id}/schedule`,
    SEND: (id: number) => `/admin/scheduled-messages/${id}/send`,
    CANCEL: (id: number) => `/admin/scheduled-messages/${id}/cancel`,
    DELETE: (id: number) => `/admin/scheduled-messages/${id}`,
  },
  COURSEGROUPS: {
    GET: (skip: number, take: number, filter: string = "", orderBy: string = "") =>
      `/admin/coursegroups?${queryParamsToText([
        ...basePagingToQueryParams(skip, take, ""),
        { name: "filter", value: filter },
        { name: "orderBy", value: orderBy },
      ])}`,
    GET_BY_ID: (courseGroupId: number) => `/admin/coursegroups/${courseGroupId}`,
    DELETE: (courseGroupId: number) => `/admin/coursegroups/${courseGroupId}`,
    UPDATE_FIELD: (courseGroupId: number, courseGroupFieldId: number) =>
      `/admin/coursegroups/${courseGroupId}/fields/${courseGroupFieldId}`,
    CREATE_FIELD: (courseGroupId: number) => `/admin/coursegroups/${courseGroupId}/fields`,
    UPDATE_CONTACT_PERSONS: (courseGroupId: number) => `/admin/coursegroups/${courseGroupId}/contact-persons`,
    DELETE_FIELD: (courseGroupId: number, courseGroupFieldId: number) =>
      `/admin/coursegroups/${courseGroupId}/fields/${courseGroupFieldId}`,
    CREATE: () => `/admin/coursegroups`,
    UPDATE: (courseGroupId: number) => `/admin/coursegroups/${courseGroupId}`,
    GET_COURSES: (courseGroupId: number) => `/admin/coursegroups/${courseGroupId}/courses`,
    GET_TRAINERS: (courseGroupId: number) => `/admin/coursegroups/${courseGroupId}/trainers`,
    GET_RECIPIENTS: (courseGroupId: number) => `/admin/coursegroups/${courseGroupId}/recipients`,
    UPDATE_BLACKLISTED_TRAINERS: (courseGroupId: number) => `/admin/coursegroups/${courseGroupId}/blacklisted-trainers`,
    GET_PROGRAM: (courseGroupId: number) => `/admin/coursegroups/${courseGroupId}/program`,
    UPDATE_PROGRAM: (courseGroupId: number) => `/admin/coursegroups/${courseGroupId}/program`,
    PREPOPULATE_PROGRAM: (courseGroupId: number) => `/admin/coursegroups/${courseGroupId}/prepopulate-data`,
    GET_FAQ: (courseGroupId: number) => `/admin/coursegroups/${courseGroupId}/faq`,
    UPDATE_FAQ: (courseGroupId: number) => `/admin/coursegroups/${courseGroupId}/faq`,
    BULK_ASSIGN_CATEGORY: () => `/admin/coursegroups/bulk-assign-category`,
    GET_IMAGE_SIZES: () => `/admin/coursegroups/image-sizes`,
    SHOP_SETTINGS: () => `/admin/coursegroups/shop-settings`,
    UPDATE_SHOP_SETTINGS: (courseGroupId: number) => `/admin/coursegroups/${courseGroupId}/shop-settings`,
    WAITLIST: (courseGroupId: number) => `/admin/coursegroups/${courseGroupId}/waitlist`,
  },
  NOTIFICATIONS: {
    GET: (limit: number, skip: number, filter?: string) =>
      `/admin/notifications?${queryParamsToText([
        ...basePagingToQueryParams(skip, limit, ""),
        { name: "filter", value: filter },
      ])}`,
    GET_UNREAD_COUNT: (category?: number[]) =>
      `/admin/notifications/unread/count?${queryParamsToText([
        ...(category && category.length > 0 ? category.map((c) => ({ name: "category", value: c.toString() })) : []),
      ])}`,
    MARK_AS_READ: (id: number) => `/admin/notifications/${id}/read`,
    MARK_ALL_AS_READ: (category?: number[]) =>
      `/admin/notifications/read-all?${queryParamsToText([
        ...(category && category.length > 0 ? category.map((c) => ({ name: "category", value: c.toString() })) : []),
      ])}`,
  },
  COURSEFORM: {
    GET_ALL: (isTemplate?: boolean) =>
      `/admin/courses/forms/groups${isTemplate !== undefined ? `?isTemplate=${isTemplate}` : ""}`,
    GET_BY_TYPE: (courseId: number, type: number) => `/admin/courses/${courseId}/forms?type=${type}`,
    ADD_FIELD: (courseId: number, formId: number, groupId: number) =>
      `/admin/courses/${courseId}/forms/${formId}/groups/${groupId}/fields`,
    UPDATE_FIELD: (courseId: number, formId: number, groupId: number, fieldId: number) =>
      `/admin/courses/${courseId}/forms/${formId}/groups/${groupId}/fields/${fieldId}`,
    GET_FIELDS: (courseId: number, formId: number) => `/admin/courses/${courseId}/forms/${formId}/fields`,
    DELETE_FIELD: (courseId: number, formId: number, groupId: number, fieldId: number) =>
      `/admin/courses/${courseId}/forms/${formId}/groups/${groupId}/fields/${fieldId}`,
    ADD_GROUP: (courseId: number, formId: number) => `/admin/courses/${courseId}/forms/${formId}/groups`,
    UPDATE_GROUP: (courseId: number, formId: number, groupId: number) =>
      `/admin/courses/${courseId}/forms/${formId}/groups/${groupId}`,
    COPY_GROUP: (courseId: number, formId: number, copyCourseFormGroupId: number) =>
      `/admin/courses/${courseId}/forms/${formId}/groups/copy/${copyCourseFormGroupId}`,
    DELETE_GROUP: (courseId: number, formId: number, groupId: number) =>
      `/admin/courses/${courseId}/forms/${formId}/groups/${groupId}`,
  },
  CALENDAR: {
    GET: () => `/admin/calendar`,
  },
  COURSE_REGISTRATIONS: {
    MOVE: (courseId: number, registrationId: number) =>
      `/admin/courses/${courseId}/registrations/${registrationId}/move`,
    DELETE: (courseId: number, registrationId: number) => `/admin/courses/${courseId}/registrations/${registrationId}`,
    UPDATE_DUE_DATES: (courseId: number) => `/admin/courses/${courseId}/registrations/update-due-dates`,
    GET_REGISTRATION_ID: (courseId: number, userId: number) =>
      `/admin/courses/${courseId}/users/${userId}/registration-id`,
    DISMISS_CANCELLATION_REQUEST: (courseId: number, registrationId: number) =>
      `/admin/courses/${courseId}/registrations/${registrationId}/dismiss-cancellation-request`,
    REVENUE_SUMMARY: (courseId: number, registrationId: number) =>
      `/admin/courses/${courseId}/registrations/${registrationId}/revenue-summary`,
    REMOVED_REVENUE: (courseId: number) => `/admin/courses/${courseId}/registrations/removed-revenue`,
    RETAINED_REVENUE: (courseId: number, registrationId: number) =>
      `/admin/courses/${courseId}/registrations/${registrationId}/retained-revenue`,
  },
  REGISTRATIONS: {
    GET: (skip: number, take: number, searchPhrase: string, filter: string, orderBy?: string) =>
      `/admin/registrations?${queryParamsToText([
        ...basePagingToQueryParams(skip, take, searchPhrase),
        { name: "filter", value: filter },
        { name: "orderBy", value: orderBy },
      ])}`,
    GET_BY_COURSE: (courseId: number) => `/admin/courses/${courseId}/registrations`,
    GET_APPLICATION_FORM: (studentId: number, courseId: number) =>
      `/admin/students/${studentId}/courses/${courseId}/application-form?editable=true`,
    UPDATE_FIELD: (studentId: number, courseId: number, fieldId: number) =>
      `/admin/students/${studentId}/courses/${courseId}/application-form/fields/${fieldId}`,
    UPDATE_FORM: (studentId: number, courseId: number) =>
      `/admin/students/${studentId}/courses/${courseId}/application-form`,
  },
  COURSE_DOCUMENTS: {
    CREATE: (courseId: number) => `/admin/courses/${courseId}/documents`,
    UPDATE: (courseId: number, courseDocumentId: number) => `/admin/courses/${courseId}/documents/${courseDocumentId}`,
    DELETE: (courseId: number, courseDocumentId: number) => `/admin/courses/${courseId}/documents/${courseDocumentId}`,
    GET: (courseId: number) => `/admin/courses/${courseId}/documents`,
    GET_BY_ID: (courseId: number, courseDocumentId: number) =>
      `/admin/courses/${courseId}/documents/${courseDocumentId}`,
    GET_STUDENTS: (courseId: number, courseDocumentId: number, filter?: string) =>
      `/admin/courses/${courseId}/documents/${courseDocumentId}/students?${queryParamsToText([
        { name: "filter", value: filter },
      ])}`,
    GENERATE_DOCUMENTS: (courseId: number, courseDocumentId: number) =>
      `/admin/courses/${courseId}/documents/${courseDocumentId}/students`,
    EXTRA_PAGES: {
      GET: (courseId: number, courseDocumentId: number) =>
        `/admin/courses/${courseId}/documents/${courseDocumentId}/extra-pages`,
      CREATE: (courseId: number, courseDocumentId: number) =>
        `/admin/courses/${courseId}/documents/${courseDocumentId}/extra-pages`,
      DELETE: (courseId: number, courseDocumentId: number, pageId: number) =>
        `/admin/courses/${courseId}/documents/${courseDocumentId}/extra-pages/${pageId}`,
      REORDER: (courseId: number, courseDocumentId: number) =>
        `/admin/courses/${courseId}/documents/${courseDocumentId}/extra-pages/reorder`,
    },
    // The uploaded PDF of an extra page (v1 and v2 documents), as bytes.
    EXTRA_PAGE_FILE: (courseId: number, courseDocumentId: number, pageId: number) =>
      `/admin/courses/${courseId}/documents/${courseDocumentId}/extra-pages/${pageId}/file`,
  },
  // "Dokumenty 2.0" (pdfme) course documents; the numbers, student data and PDFs of one document live under its id.
  COURSE_DOCUMENTS_V2: {
    CREATE: (courseId: number) => `/admin/courses/${courseId}/documents/v2`,
    UPDATE: (courseId: number, courseDocumentId: number) => `/admin/courses/${courseId}/documents/v2/${courseDocumentId}`,
    STUDENTS: (courseId: number, courseDocumentId: number) =>
      `/admin/courses/${courseId}/documents/v2/${courseDocumentId}/students`,
    ISSUES: (courseId: number, courseDocumentId: number) =>
      `/admin/courses/${courseId}/documents/v2/${courseDocumentId}/issues`,
    STUDENT_PHOTO: (courseId: number, courseDocumentId: number, studentId: number) =>
      `/admin/courses/${courseId}/documents/v2/${courseDocumentId}/students/${studentId}/photo`,
    STUDENT_FILE: (courseId: number, courseDocumentId: number, studentId: number) =>
      `/admin/courses/${courseId}/documents/v2/${courseDocumentId}/students/${studentId}/file`,
  },
  COURSE_TRAINERS: {
    GET_BY_COURSEID: (courseId: number) => `/admin/courses/${courseId}/trainers`,
  },
  COURSE_ACTIONS: {
    GET_BY_COURSEID: (courseId: number) => `/admin/courses/${courseId}/actions`,
  },
  COURSE_SURVEYS: {
    SAVE: (courseId: number) => `/admin/courses/${courseId}/surveys`,
  },
  COURSE_RATINGS: {
    GET_BY_COURSEID: (courseId: number) => `/admin/courses/${courseId}/ratings`,
  },
  COURSE_TERMS: {
    GET_BY_COURSEID: (courseId: number) => `/admin/courses/${courseId}/terms`,
    CREATE: (courseId: number) => `/admin/courses/${courseId}/terms`,
    UPDATE: (courseId: number, courseTermsId: number) => `/admin/courses/${courseId}/terms/${courseTermsId}`,
    DELETE: (courseId: number, courseTermsId: number) => `/admin/courses/${courseId}/terms/${courseTermsId}`,
  },
  COURSE_RESOURCES: {
    GET_BY_COURSEID: (courseId: number) => `/admin/courses/${courseId}/resources`,
    GET_TAB_BY_ID: (courseId: number, courseResourceTabId: number) =>
      `/admin/courses/${courseId}/resources/tabs/${courseResourceTabId}`,
    CREATE_LINK_RESOURCE: (courseId: number, courseResourceTabId: number) =>
      `/admin/courses/${courseId}/resources/tabs/${courseResourceTabId}/links`,
    CREATE_DOCUMENT_RESOURCE: (courseId: number, courseResourceTabId: number) =>
      `/admin/courses/${courseId}/resources/tabs/${courseResourceTabId}/documents`,
    CREATE_FILE_RESOURCE: (courseId: number, courseResourceTabId: number) =>
      `/admin/courses/${courseId}/resources/tabs/${courseResourceTabId}/files`,
    DELETE: (courseId: number, resourceId: number) => `/admin/courses/${courseId}/resources/${resourceId}`,
    CREATE_TAB: (courseId: number) => `/admin/courses/${courseId}/resources/tabs`,
    UPDATE_TAB: (courseId: number, courseResourceTabId: number) =>
      `/admin/courses/${courseId}/resources/tabs/${courseResourceTabId}`,
    DELETE_TAB: (courseId: number, courseResourceTabId: number) =>
      `/admin/courses/${courseId}/resources/tabs/${courseResourceTabId}`,
    UPDATE_RESOURCE_LINK: (courseId: number, resourceId: number) =>
      `/admin/courses/${courseId}/resources/${resourceId}/links`,
    UPDATE_RESOURCE_FILE: (courseId: number, resourceId: number) =>
      `/admin/courses/${courseId}/resources/${resourceId}/files`,
    UPDATE_FOLDERS: (courseId: number) => `/admin/courses/${courseId}/resources/folders`,
    DOWNLOAD_RESOURCE: (courseId: number, resourceId: number) => `/courses/${courseId}/resources/${resourceId}`,
    UPDATE_PAYMENT_POLICY: (courseId: number) => `/admin/courses/${courseId}/resources/payment-policy`,
  },
  SURVEYS: {
    GET: (skip: number, take: number, searchPhrase: string, filter: string, orderBy: string) =>
      `/admin/surveys?${queryParamsToText([
        ...basePagingToQueryParams(skip, take, searchPhrase),
        { name: "filter", value: filter },
        { name: "orderBy", value: orderBy },
      ])}`,
    GET_BY_ID: (surveyId: number) => `/admin/surveys/${surveyId}`,
    CREATE: () => `/admin/surveys`,
    DELETE: (surveyId: number) => `/admin/surveys/${surveyId}`,
    UPDATE: (surveyId: number) => `/admin/surveys/${surveyId}`,
    UPDATE_QUESTION: (surveyId: number, questionId: number) => `/admin/surveys/${surveyId}/questions/${questionId}`,
    CREATE_QUESTION: (surveyId: number) => `/admin/surveys/${surveyId}/questions`,
    DELETE_QUESTION: (surveyId: number, questionId: number) => `/admin/surveys/${surveyId}/questions/${questionId}`,
    REORDER_QUESTIONS: (surveyId: number) => `/admin/surveys/${surveyId}/questions/order`,
  },
  EXAMS: {
    GET: (skip: number, take: number, filter: string, orderBy: string) =>
      `/admin/exams?${queryParamsToText([
        ...basePagingToQueryParams(skip, take, ""),
        { name: "filter", value: filter },
        { name: "orderBy", value: orderBy },
      ])}`,
    GET_BY_ID: (examId: number) => `/admin/exams/${examId}`,
    CREATE: () => `/admin/exams`,
    DUPLICATE: (examId: number) => `/admin/exams/${examId}/duplicate`,
    DELETE: (examId: number) => `/admin/exams/${examId}`,
    UPDATE: (examId: number) => `/admin/exams/${examId}`,
    UPDATE_QUESTION: (examId: number, questionId: number) => `/admin/exams/${examId}/questions/${questionId}`,
    CREATE_QUESTION: (examId: number) => `/admin/exams/${examId}/questions`,
    DELETE_QUESTION: (examId: number, questionId: number) => `/admin/exams/${examId}/questions/${questionId}`,
  },
  TRAINER_TOPICS: {
    GET_BY_TRAINERID: (trainerId: number) => `/admin/trainers/${trainerId}/topics`,
  },
  COURSE_EXAMS: {
    GET_BY_COURSEID: (courseId: number) => `/admin/courses/${courseId}/exams`,
    GET_WITH_STUDENTS_BY_COURSE_ID: (courseId: number) => `/admin/courses/${courseId}/exams/students`,
    GET_WITH_STUDENTS_BY_ID: (courseId: number, courseExamId: number) =>
      `/admin/courses/${courseId}/exams/${courseExamId}/students`,
    CREATE: (courseId: number) => `/admin/courses/${courseId}/exams`,
    DELETE: (courseId: number, examId: number) => `/admin/courses/${courseId}/exams/${examId}`,
    UPDATE: (courseId: number, examId: number) => `/admin/courses/${courseId}/exams/${examId}`,
    PUBLISH_RESULTS: (courseId: number) => `/admin/courses/${courseId}/exams/publish-results`,
  },
  USER_EXAMS: {
    GET_USER_EXAM: (courseId: number, courseExamId: number, userId: number, userExamId: number) =>
      `/admin/courses/${courseId}/courseExams/${courseExamId}/users/${userId}/userExams/${userExamId}`,
    CANCEL_PAYMENT: (courseId: number, courseExamId: number, userId: number) =>
      `/admin/courses/${courseId}/courseExams/${courseExamId}/users/${userId}/cancel-payment`,
    REVIEW_USER_EXAM: (courseId: number, courseExamId: number, userId: number, userExamId: number) =>
      `/admin/courses/${courseId}/courseExams/${courseExamId}/users/${userId}/userExams/${userExamId}/review`,
    UPDATE_REVIEW: (courseId: number, courseExamId: number, userId: number, userExamId: number) =>
      `/admin/courses/${courseId}/courseExams/${courseExamId}/users/${userId}/userExams/${userExamId}/review`,
    GET_USER_EXAMS: (courseId: number, userId: number) =>
      `/admin/courses/${courseId}/courseExams/users/${userId}/exams`,
    DOWNLOAD_FILE: (courseId: number, courseExamId: number, userId: number, userExamId: number, fileId: number) =>
      `/admin/courses/${courseId}/courseExams/${courseExamId}/users/${userId}/userExams/${userExamId}/files/${fileId}`,
  },
  COURSE_FINANCES: {
    GET_BY_CATEGORIES: (courseId: number | string) => `admin/courses/${courseId}/finances/by-categories`,
    GET_RECONCILIATION: (courseId: number | string) => `admin/courses/${courseId}/finances/reconciliation`,
    GET_BY_COURSE_ID: (courseId: number) => `/admin/courses/${courseId}/finances`,
    GET: (skip: number, take: number, searchPhrase: string, filter: string) =>
      `/admin/coursefinances?${queryParamsToText([
        ...basePagingToQueryParams(skip, take, searchPhrase),
        { name: "filter", value: filter },
      ])}`,
    GET_BY_ID: (courseId: number, courseFinanceId: number) => `/admin/courses/${courseId}/finances/${courseFinanceId}`,
    CREATE: (courseId: number | string) => `admin/courses/${courseId}/finances`,
    UPDATE: (courseId: number, courseFinanceId: number) => `/admin/courses/${courseId}/finances/${courseFinanceId}`,
    DELETE: (courseId: number, courseFinanceId: number) => `/admin/courses/${courseId}/finances/${courseFinanceId}`,
    DOWNLOAD_INVOICE: (courseId: number, courseFinanceId: number) =>
      `/admin/courses/${courseId}/finances/${courseFinanceId}/invoice`,
  },
  // TODO! ${isSystem !== undefined ? `&isSystem=${isSystem}` : ""}`,

  COURSE_FINANCE_CATEGORIES: {
    GET: (skip: number, take: number, searchPhrase: string, filter: string = "", orderBy: string = "") =>
      `/admin/coursefinancecategories?${queryParamsToText([
        ...basePagingToQueryParams(skip, take, searchPhrase),
        { name: "filter", value: filter },
        { name: "orderBy", value: orderBy },
      ])}`,
    GET_BY_ID: (id: number) => `/admin/coursefinancecategories/${id}`,
    CREATE: () => `/admin/coursefinancecategories`,
    UPDATE: (id: number) => `/admin/coursefinancecategories/${id}`,
    DELETE: (courseId: number) => `/admin/coursefinancecategories/${courseId}`,
  },
  COURSES: {
    GET: (skip: number, take: number, searchPhrase: string, filter: string = "", orderBy: string = "") =>
      `/admin/courses?${queryParamsToText([
        { name: "filter", value: filter },
        { name: "orderBy", value: orderBy },
        ...basePagingToQueryParams(skip, take, searchPhrase),
      ])}`,
    GET_BY_ID: (id: number) => `/admin/courses/${id}`,
    CHANGE_STATUS_TO_PLANNED: (courseId: number) => `/admin/courses/${courseId}/status-planned`,
    CHANGE_STATUS_TO_ARCHIVED: (courseId: number) => `/admin/courses/${courseId}/status-archived`,
    CHANGE_STATUS_TO_DRAFT: (courseId: number) => `/admin/courses/${courseId}/status-draft`,
    CHANGE_STATUS_TO_READY: (courseId: number) => `/admin/courses/${courseId}/status-ready`,
    CHANGE_STATUS_TO_ONGOING: (courseId: number) => `/admin/courses/${courseId}/status-ongoing`,
    UPDATE_PREVIEW_DATA: (courseId: number) => `/admin/courses/${courseId}/preview-data`,
    DUPLICATE: (courseId: number) => `/admin/courses/${courseId}/duplicate`,
    CREATE: () => `/admin/courses`,
    UPDATE: (courseId: number) => `/admin/courses/${courseId}`,
    DELETE: (courseId: number) => `/admin/courses/${courseId}`,
    GET_RECIPIENTS: (courseId: number) => `/admin/courses/${courseId}/recipients`,
    UPDATE_REGISTRATION_SETTINGS: (courseId: number) => `/admin/courses/${courseId}/registration-settings`,
  },
  COURSE_STATUS: {
    GET_BY_ID: (courseId: number) => `/admin/course-status/${courseId}`,
    SESSIONS_READY: (courseId: number) => `/admin/course-status/${courseId}/sessions-ready`,
    SESSIONS_UNREADY: (courseId: number) => `/admin/course-status/${courseId}/sessions-unready`,
    TRANCHES_READY: (courseId: number) => `/admin/course-status/${courseId}/tranches-ready`,
    TRANCHES_UNREADY: (courseId: number) => `/admin/course-status/${courseId}/tranches-unready`,
    EXAMS_READY: (courseId: number) => `/admin/course-status/${courseId}/exams-ready`,
    EXAMS_UNREADY: (courseId: number) => `/admin/course-status/${courseId}/exams-unready`,
    DOCUMENTS_READY: (courseId: number) => `/admin/course-status/${courseId}/documents-ready`,
    DOCUMENTS_UNREADY: (courseId: number) => `/admin/course-status/${courseId}/documents-unready`,
    APPLICATION_FORM_READY: (courseId: number) => `/admin/course-status/${courseId}/application-form-ready`,
    APPLICATION_FORM_UNREADY: (courseId: number) => `/admin/course-status/${courseId}/application-form-unready`,
    PUBLIC_DATA_READY: (courseId: number) => `/admin/course-status/${courseId}/public-data-ready`,
    PUBLIC_DATA_UNREADY: (courseId: number) => `/admin/course-status/${courseId}/public-data-unready`,
  },
  COURSESTRANCHES: {
    CREATE: (courseId: number) => `/admin/courses/${courseId}/tranches`,
    GET: (courseId: number) => `/admin/courses/${courseId}/tranches`,
    UPDATE: (courseId: number, trancheId: number) => `/admin/courses/${courseId}/tranches/${trancheId}`,
    GET_POLICIES: (courseId: number) => `/admin/courses/${courseId}/tranches/policies`,
    CREATE_POLICY: (courseId: number) => `/admin/courses/${courseId}/tranches/policies`,
    DELETE_POLICY: (courseId: number, policyId: number) => `/admin/courses/${courseId}/tranches/policies/${policyId}`,
    DELETE: (courseId: number, trancheId: number) => `/admin/courses/${courseId}/tranches/${trancheId}`,
    GENERATE_PAYMENT_LINK: (courseId: number) => `/admin/courses/${courseId}/tranches/generate-payment-link`,
  },
  COURSES_STUDENTS: {
    GET: (courseId: number, skip: number, take: number, searchPhrase: string) =>
      `/admin/courses/${courseId}/students?${queryParamsToText(basePagingToQueryParams(skip, take, searchPhrase))}`,
    GET_DOCUMENTS: (courseId: number) => `/admin/courses/${courseId}/students/documents`,
    DOWNLOAD: (courseId: number, courseDocumentFileId: number) =>
      `/admin/courses/${courseId}/students/documents/${courseDocumentFileId}/download`,
    INVITE: (courseId: number) => `/admin/courses/${courseId}/students/invite`,
    ZIP_FILES: (courseId: number, courseDocumentFileId: number) =>
      `/admin/courses/${courseId}/students/documents/${courseDocumentFileId}/download`,
    ZIP_DOWNLOAD: (courseId: number) => `/admin/courses/${courseId}/documents/download/zip`,
    PDF_MERGE: (courseId: number) => `/admin/courses/${courseId}/documents/download/merge`,
    GET_END_SURVEY: (courseId: number, userId: number) =>
      `/admin/courses/${courseId}/students/user-surveys/${userId}/endCourseSurvey`,
  },
  COURSES_ATTENDANCE: {
    GET: (courseId: number, day: string) => `/admin/courses/${courseId}/attendance?day=${encodeURIComponent(day)}`,
    GET_DAYS: (courseId: number) => `/admin/courses/${courseId}/attendance/days`,
  },
  SCHEDULE_EVENTS: {
    GET: (courseId: number) => `/admin/courses/${courseId}/schedule-events`,
  },
  COURSESSCHEDULES: {
    CONFLICTS: (courseId: number) => `/admin/courses/${courseId}/schedules/conflicts`,
    CHECK_CONFLICTS: (courseId: number) => `/admin/courses/${courseId}/schedules/conflicts/check`,
    CREATE_TOPIC: (courseId: number) => `/admin/courses/${courseId}/schedules/topic`,
    UPDATE_TOPIC: (courseId: number, courseScheduleId: number) =>
      `/admin/courses/${courseId}/schedules/${courseScheduleId}/topic`,
    CREATE_TOPIC_GROUP: (courseId: number) => `/admin/courses/${courseId}/schedules/topicgroup`,
    UPDATE_TOPIC_GROUP: (courseId: number, courseScheduleId: number) =>
      `/admin/courses/${courseId}/schedules/${courseScheduleId}/topicgroup`,
    CREATE_BREAK: (courseId: number) => `/admin/courses/${courseId}/schedules/break`,
    UPDATE_BREAK: (courseId: number, courseScheduleId: number) =>
      `/admin/courses/${courseId}/schedules/${courseScheduleId}/break`,
    GET_BY_ID: (courseId: number, courseScheduleId: number) =>
      `/admin/courses/${courseId}/schedules/${courseScheduleId}`,
    DELETE: (courseId: number, courseScheduleId: number) => `/admin/courses/${courseId}/schedules/${courseScheduleId}`,
    BULK_UPDATE_PLACES: (courseId: number) => `/admin/courses/${courseId}/schedules/places`,
    BULK_DELETE: (courseId: number) => `/admin/courses/${courseId}/schedules/bulk`,
    HISTORY: (courseId: number) => `/admin/courses/${courseId}/schedules/history`,
  },
  TRAINER_COSTS: {
    GET_ENTRIES: (courseId: number) => `/admin/courses/${courseId}/trainer-costs/entries`,
    CREATE: (courseId: number) => `/admin/courses/${courseId}/trainer-costs/entries`,
    UPDATE: (courseId: number, entryId: number) => `/admin/courses/${courseId}/trainer-costs/entries/${entryId}`,
    DELETE: (courseId: number, entryId: number) => `/admin/courses/${courseId}/trainer-costs/entries/${entryId}`,
  },
  COURSES_SESSIONS: {
    CREATE: (courseId: number) => `/admin/courses/${courseId}/sessions`,
    GET: (courseId: number) => `/admin/courses/${courseId}/sessions`,
    UPDATE: (courseId: number, courseSessionId: number) => `/admin/courses/${courseId}/sessions/${courseSessionId}`,
    DELETE: (courseId: number, courseSessionId: number) => `/admin/courses/${courseId}/sessions/${courseSessionId}`,
  },
  BLOCKED: {
    GET: (skip: number, take: number, searchPhrase: string) =>
      `/admin/blocked?${queryParamsToText(basePagingToQueryParams(skip, take, searchPhrase))}`,
    CREATE: () => `/admin/blocked`,
    DELETE: (blockedId: number) => `/admin/blocked/${blockedId}`,
  },
  DISCOUNTS: {
    GET: (skip: number, take: number, searchPhrase: string, filter?: string, orderBy?: string) =>
      `/admin/payment-discounts?${queryParamsToText([
        ...basePagingToQueryParams(skip, take, searchPhrase),
        { name: "filter", value: filter },
        { name: "orderBy", value: orderBy },
      ])}`,
    GET_REGISTRATIONS: (discountId: number) => `/admin/payment-discounts/${discountId}/registrations`,
    CREATE: () => `/admin/payment-discounts`,
    DELETE: (discountId: number) => `/admin/payment-discounts/${discountId}`,
    GET_BY_ID: (discountId: number) => `/admin/payment-discounts/${discountId}`,
    UPDATE: (discountId: number) => `/admin/payment-discounts/${discountId}`,
    VIOLATED_BUNDLES: () => `/admin/payment-discounts/violated-bundles`,
    SET_DISABLED: (discountId: number) => `/admin/payment-discounts/${discountId}/disabled`,
    // Bundle discount endpoints
    BUNDLE: {
      CREATE: () => `/admin/payment-discounts/bundle`,
      UPDATE: (discountId: number) => `/admin/payment-discounts/bundle/${discountId}`,
    },
  },
  USER_DISCOUNTS: {
    GET: (userId: number) => `/admin/users/${userId}/payment-discounts`,
    DELETE: (userId: number, discountId: number) => `/admin/users/${userId}/payment-discounts/${discountId}`,
  },
  PAYMENT_DISCOUNTS: {
    GET_AVAILABLE: (courseIds: number[]) => `/payment-discounts/available?courseIds=${courseIds.join(",")}`,
    GET_MY_BUNDLES: () => `/payment-discounts/my-bundles`,
  },
  PLACES: {
    GET: (skip: number, take: number, searchPhrase: string, filter: string, orderBy: string = "") =>
      `/admin/places?${queryParamsToText([
        ...basePagingToQueryParams(skip, take, searchPhrase),
        { name: "filter", value: filter },
        { name: "orderBy", value: orderBy },
      ])}`,
    GET_BY_ID: (placeId: number) => `/admin/places/${placeId}`,
    CREATE: () => `/admin/places`,
    UPDATE: (placeId: number) => `/admin/places/${placeId}`,
    DELETE: (placeId: number) => `/admin/places/${placeId}`,
  },
  KSEF: {
    GET: () => `/admin/ksef`,
    GET_BY_ID: (id: number) => `/admin/ksef/${id}`,
    CREATE: () => `/admin/ksef`,
    UPDATE: (id: number) => `/admin/ksef/${id}`,
    DELETE: (id: number) => `/admin/ksef/${id}`,
    TEST_CONNECTION: (id: number) => `/admin/ksef/${id}/test-connection`,
    GET_DASHBOARD: () => `/admin/ksef/dashboard`,
    GET_SUBMISSIONS: (id: number, skip: number = 0, take: number = 20, status?: number) =>
      `/admin/ksef/${id}/submissions?${queryParamsToText([
        { name: "skip", value: skip.toString() },
        { name: "take", value: take.toString() },
        { name: "status", value: status?.toString() },
      ])}`,
    GET_SUBMISSION: (id: number, submissionId: number) => `/admin/ksef/${id}/submissions/${submissionId}`,
    RETRY_SUBMISSION: (id: number, submissionId: number) => `/admin/ksef/${id}/submissions/${submissionId}/retry`,
    DOWNLOAD_UPO: (id: number, submissionId: number) => `/admin/ksef/${id}/submissions/${submissionId}/upo`,
    GET_LOGS: (id: number, skip: number = 0, take: number = 50) =>
      `/admin/ksef/${id}/logs?${queryParamsToText([
        { name: "skip", value: skip.toString() },
        { name: "take", value: take.toString() },
      ])}`,
    UPLOAD_CERTIFICATE: (id: number) => `/admin/ksef/${id}/certificate`,
  },
  INTEGRATIONS: {
    GET: (skip: number, take: number, searchPhrase: string, filter: string, orderBy: string) =>
      `/admin/integrations?${queryParamsToText([
        ...basePagingToQueryParams(skip, take, searchPhrase),
        { name: "filter", value: filter },
        { name: "orderBy", value: orderBy },
      ])}`,
    GET_BY_ID: (integrationId: number) => `/admin/integrations/${integrationId}`,
    GET_PRODUCTS: (
      integrationId: number,
      skip: number,
      take: number,
      searchPhrase: string,
      filter: string,
      orderBy: string,
    ) =>
      `/admin/integrations/${integrationId}/products?${queryParamsToText([
        ...basePagingToQueryParams(skip, take, searchPhrase),
        { name: "filter", value: filter },
        { name: "orderBy", value: orderBy },
      ])}`,
    CREATE_COURSE_PRODUCT: (integrationId: number, courseId: number) =>
      `/admin/integrations/${integrationId}/courses/${courseId}/products`,
    UPDATE_PRODUCT_FIELDS: (integrationId: number) => `/admin/integrations/${integrationId}/product-fields`,
    ASSIGN_CATEGORIES: (integrationId: number) => `/admin/integrations/${integrationId}/assign-categories`,
    GET_PRODUCT_BY_EXTERNAL_ID: (integrationId: number, externalId: number) =>
      `/admin/integrations/${integrationId}/products/${externalId}`,
    GET_CATEGORIES: (integrationId: number) => `/admin/integrations/${integrationId}/categories`,
    GET_CATEGORIES_MAPPINGS: (integrationId: number) => `/admin/integrations/${integrationId}/categories-mappings`,
    UPDATE_CATEGORIES_MAPPINGS: (integrationId: number) => `/admin/integrations/${integrationId}/categories-mappings`,
    CREATE: () => `/admin/integrations`,
    UPDATE: (integrationId: number) => `/admin/integrations/${integrationId}`,
    DELETE: (integrationId: number) => `/admin/integrations/${integrationId}`,
    GET_COURSE_GROUP_PRODUCTS: (
      integrationId: number,
      courseGroupId: number,
      skip: number,
      take: number,
      filter: string = "",
      orderBy: string = "",
    ) =>
      `/admin/integrations/${integrationId}/course-groups/${courseGroupId}/products?${queryParamsToText([
        ...basePagingToQueryParams(skip, take, ""),
        { name: "filter", value: filter },
        { name: "orderBy", value: orderBy },
      ])}`,
    GET_LOGS: (integrationId: number, skip: number = 0, take: number = 20) =>
      `/admin/integrations/${integrationId}/logs?${queryParamsToText([
        { name: "skip", value: skip.toString() },
        { name: "take", value: take.toString() },
      ])}`,
    GET_COURSE_GROUP_LOGS: (integrationId: number, courseGroupId: number, skip: number = 0, take: number = 20) =>
      `/admin/integrations/${integrationId}/course-groups/${courseGroupId}/logs?${queryParamsToText([
        { name: "skip", value: skip.toString() },
        { name: "take", value: take.toString() },
      ])}`,
    GET_STATUS: (integrationId: number) => `/admin/integrations/${integrationId}/status`,
    TEST_CONNECTION: (integrationId: number) => `/admin/integrations/${integrationId}/test-connection`,
  },
  STUDENT_DOCUMENTS: {
    GET_BY_ID: (studentId: number) => `/admin/students/${studentId}/documents`,
  },
  DOCUMENT_TYPES: {
    GET: (skip: number, take: number, searchPhrase: string, filter: string = "", orderBy: string = "") =>
      `/admin/documenttypes?${queryParamsToText([
        ...basePagingToQueryParams(skip, take, searchPhrase),
        { name: "filter", value: filter },
        { name: "orderBy", value: orderBy },
      ])}`,
    GET_BY_ID: (documentTypeId: number) => `/admin/documenttypes/${documentTypeId}`,
    CREATE: () => `/admin/documenttypes`,
    UPDATE: (documentTypeId: number) => `/admin/documenttypes/${documentTypeId}`,
    DELETE: (documentTypeId: number) => `/admin/documenttypes/${documentTypeId}`,
  },
  PLACETAGS: {
    GET: (skip: number, take: number, searchPhrase: string, filter: string, orderBy: string) =>
      `/admin/placetags?${queryParamsToText([
        ...basePagingToQueryParams(skip, take, searchPhrase),
        { name: "filter", value: filter },
        { name: "orderBy", value: orderBy },
      ])}`,
    GET_BY_ID: (placeTagId: number) => `/admin/placetags/${placeTagId}`,
    CREATE: () => `/admin/placetags`,
    UPDATE: (placeTagId: number) => `/admin/placetags/${placeTagId}`,
    DELETE: (placeTagId: number) => `/admin/placetags/${placeTagId}`,
  },
  EMAIL_TEMPLATES: {
    GET: (skip: number, take: number, searchPhrase: string, filter: string = "", orderBy: string = "") =>
      `/admin/emailtemplates?${queryParamsToText([
        ...basePagingToQueryParams(skip, take, searchPhrase),
        { name: "filter", value: filter },
        { name: "orderBy", value: orderBy },
      ])}`,
    CREATE: () => `/admin/emailtemplates`,
    UPDATE: (emailTemplateId: number) => `/admin/emailtemplates/${emailTemplateId}`,
    DELETE: (emailTemplateId: number) => `/admin/emailtemplates/${emailTemplateId}`,
    GET_BY_ID: (emailTemplateId: number) => `/admin/emailtemplates/${emailTemplateId}`,
    GET_TRIGGERS: () => `/admin/emailtemplates/triggers`,
    DUPLICATE: (emailTemplateId: number) => `/admin/emailtemplates/${emailTemplateId}/duplicate`,
  },
  EMAIL_PARAMETERS: {
    GET: (skip: number, take: number, searchPhrase: string) =>
      `/admin/emailparameters?${queryParamsToText(basePagingToQueryParams(skip, take, searchPhrase))}`,
    CREATE: () => `/admin/emailparameters`,
    UPDATE: (emailParameterId: number) => `/admin/emailparameters/${emailParameterId}`,
    DELETE: (emailParameterId: number) => `/admin/emailparameters/${emailParameterId}`,
    GET_BY_ID: (emailParameterId: number) => `/admin/emailparameters/${emailParameterId}`,
  },
  INVITED_STUDENTS: {
    GET_NOT_LOGGED: () => `/admin/students/invited/not-logged`,
    SEND_REMINDERS: () => `/admin/students/invited/send-reminders`,
  },
  STUDENT_COURSES: {
    GET: (studentId: number) => `/admin/students/${studentId}/courses`,
    GET_BY_ID: (studentId: number, courseId: number) => `/admin/students/${studentId}/courses/${courseId}`,
    REGISTER: (studentId: number) => `/admin/students/${studentId}/courses`,
  },
  STUDENT_FORMS: {
    GET_APPLICATION_FORM: (studentId: number, courseId: number) =>
      `/admin/students/${studentId}/courses/${courseId}/application-form`,
    UPDATE_BUYER_FIELDS: (studentId: number, courseId: number) =>
      `/admin/students/${studentId}/courses/${courseId}/application-form/buyer-fields`,
  },
  ISSUES: {
    CREATE: "/admin/issues",
    UPDATE: (id: number) => `/admin/issues/${id}`,
    GET: (filter: string) => `/admin/issues?${queryParamsToText([{ name: "filter", value: filter }])}`,
    GET_BY_ID: (id: number) => `/admin/issues/${id}`,
    CHANGE_STATUS: (id: number) => `/admin/issues/${id}/status`,
    COMMENT: (id: number) => `/admin/issues/${id}/comment`,
  },
  SETTINGS: {
    GET: "/admin/settings",
    CLIENT_APP: "/admin/settings/client-app",
    SMTP: "/admin/settings/smtp",
    SMS: "/admin/settings/sms",
    AUTOPAY: "/admin/settings/autopay",
    TRAINER_ZONE_ADMIN_NOTIFICATION_EMAIL: "/admin/settings/trainer-zone-admin-notification-email",
    INVOICE_SELLER: "/admin/settings/invoice-seller",
    INVOICE_MODE: "/admin/settings/invoice-mode",
    DOCUMENTS_V2: "/admin/settings/documents-v2",
    AUTOPAY_REFUNDS: "/admin/settings/autopay-refunds",
  },
  DOCUMENT_TEMPLATES: {
    CREATE: `/admin/documenttemplates`,
    CRAETE_PAGE: (documentTemplateId: number) => `/admin/documenttemplates/${documentTemplateId}/pages`,
    GET: (skip: number, take: number, searchPhrase: string, filter: string = "", orderBy: string = "") =>
      `/admin/documenttemplates?${queryParamsToText([
        ...basePagingToQueryParams(skip, take, searchPhrase),
        { name: "filter", value: filter },
        { name: "orderBy", value: orderBy },
      ])}`,
    GET_BY_ID: (documentTemplateId: number) => `/admin/documenttemplates/${documentTemplateId}`,
    DELETE: (documentTemplateId: number) => `/admin/documenttemplates/${documentTemplateId}`,
    UPDATE: (documentTemplateId: number) => `/admin/documenttemplates/${documentTemplateId}`,
    DOWNLOAD: (documentTemplateId: number) => `/admin/documenttemplates/${documentTemplateId}/download`,
    UPDATE_PAGE: (documentTemplateId: number, pageId: number) =>
      `/admin/documenttemplates/${documentTemplateId}/pages/${pageId}`,
    DELETE_PAGE: (documentTemplateId: number, pageId: number) =>
      `/admin/documenttemplates/${documentTemplateId}/pages/${pageId}`,
  },
  // "Dokumenty 2.0" templates (pdfme JSON) and the image assets they reference.
  DOCUMENT_TEMPLATES_V2: {
    LIST: `/admin/documentTemplatesV2`,
    BY_ID: (documentTemplateId: number) => `/admin/documentTemplatesV2/${documentTemplateId}`,
    DUPLICATE: (documentTemplateId: number) => `/admin/documentTemplatesV2/${documentTemplateId}/duplicate`,
    ASSETS: `/admin/documentTemplatesV2/assets`,
    ASSET: (ref: string) => `/admin/documentTemplatesV2/assets?ref=${encodeURIComponent(ref)}`,
  },
  SHOP: {
    GET: () => `/admin/shop`,
    UPDATE: () => `/admin/shop`,
    NEWSLETTER_SEND: () => `/admin/shop/newsletter-send`,
    LANDING_PAGES: {
      GET: (skip: number, take: number, searchPhrase: string) =>
        `/admin/shop/landing-pages?${queryParamsToText(basePagingToQueryParams(skip, take, searchPhrase))}`,
      GET_BY_ID: (id: number) => `/admin/shop/landing-pages/${id}`,
      CREATE: () => `/admin/shop/landing-pages`,
      UPDATE: (id: number) => `/admin/shop/landing-pages/${id}`,
      DELETE: (id: number) => `/admin/shop/landing-pages/${id}`,
      DUPLICATE: (id: number) => `/admin/shop/landing-pages/${id}/duplicate`,
      SECTIONS: {
        GET: (landingPageId: number) => `/admin/shop/landing-pages/${landingPageId}/sections`,
        CREATE: (landingPageId: number) => `/admin/shop/landing-pages/${landingPageId}/sections`,
        UPDATE: (landingPageId: number, sectionId: number) =>
          `/admin/shop/landing-pages/${landingPageId}/sections/${sectionId}`,
        DELETE: (landingPageId: number, sectionId: number) =>
          `/admin/shop/landing-pages/${landingPageId}/sections/${sectionId}`,
        REORDER: (landingPageId: number) => `/admin/shop/landing-pages/${landingPageId}/sections/reorder`,
      },
    },
    BLOG: {
      GET: (skip: number, take: number, searchPhrase: string) =>
        `/admin/shop/blog-posts?${queryParamsToText(basePagingToQueryParams(skip, take, searchPhrase))}`,
      GET_BY_ID: (id: number) => `/admin/shop/blog-posts/${id}`,
      CREATE: () => `/admin/shop/blog-posts`,
      UPDATE: (id: number) => `/admin/shop/blog-posts/${id}`,
      DELETE: (id: number) => `/admin/shop/blog-posts/${id}`,
      DUPLICATE: (id: number) => `/admin/shop/blog-posts/${id}/duplicate`,
      SECTIONS: {
        GET: (blogPostId: number) => `/admin/shop/blog-posts/${blogPostId}/sections`,
        CREATE: (blogPostId: number) => `/admin/shop/blog-posts/${blogPostId}/sections`,
        UPDATE: (blogPostId: number, sectionId: number) =>
          `/admin/shop/blog-posts/${blogPostId}/sections/${sectionId}`,
        DELETE: (blogPostId: number, sectionId: number) =>
          `/admin/shop/blog-posts/${blogPostId}/sections/${sectionId}`,
        REORDER: (blogPostId: number) => `/admin/shop/blog-posts/${blogPostId}/sections/reorder`,
      },
      CATEGORIES: {
        GET: () => `/admin/shop/blog-categories`,
        CREATE: () => `/admin/shop/blog-categories`,
        UPDATE: (id: number) => `/admin/shop/blog-categories/${id}`,
        DELETE: (id: number) => `/admin/shop/blog-categories/${id}`,
      },
      AUTHORS: {
        GET: () => `/admin/shop/authors`,
        CREATE: () => `/admin/shop/authors`,
        UPDATE: (id: number) => `/admin/shop/authors/${id}`,
        DELETE: (id: number) => `/admin/shop/authors/${id}`,
      },
      SETTINGS: {
        GET: () => `/admin/shop/blog-settings`,
        UPDATE: () => `/admin/shop/blog-settings`,
      },
    },
    IMAGES: {
      UPLOAD: () => `/admin/shop/images`,
    },
    FOOTER: {
      GET: () => `/admin/shop/footer`,
      UPDATE: () => `/admin/shop/footer`,
    },
    BREADCRUMBS: {
      GET: () => `/admin/shop/breadcrumbs`,
      UPDATE: () => `/admin/shop/breadcrumbs`,
    },
    ARCHIVED_COURSE_PAGE_SETTINGS: {
      GET: () => `/admin/shop/archived-course-page-settings`,
      UPDATE: () => `/admin/shop/archived-course-page-settings`,
    },
    GOOGLE_ANALYTICS: {
      GET: () => `/admin/shop/google-analytics`,
      UPDATE: () => `/admin/shop/google-analytics`,
    },
    META_PIXEL: {
      GET: () => `/admin/shop/meta-pixel`,
      UPDATE: () => `/admin/shop/meta-pixel`,
    },
    BUTTON_STYLES: {
      GET: () => `/admin/shop/button-styles`,
      UPDATE: () => `/admin/shop/button-styles`,
    },
    HEADING_STYLES: {
      GET: () => `/admin/shop/heading-styles`,
      UPDATE: () => `/admin/shop/heading-styles`,
    },
    MENU: {
      GET: () => `/admin/shop/menu`,
      UPDATE: () => `/admin/shop/menu`,
    },
    PRODUCT_CARD_SETTINGS: {
      GET: () => `/admin/shop/product-card-settings`,
      UPDATE: () => `/admin/shop/product-card-settings`,
    },
    COURSE_DETAIL_PAGE_CONFIG: {
      GET: () => `/admin/shop/course-detail-page-config`,
      UPDATE: () => `/admin/shop/course-detail-page-config`,
    },
    SEO_SETTINGS: {
      GET: () => `/admin/shop/seo-settings`,
      UPDATE: () => `/admin/shop/seo-settings`,
    },
    NEWSLETTER_SUBSCRIPTIONS: {
      GET: (skip: number, take: number) => `/admin/shop/newsletter-subscriptions?skip=${skip}&take=${take}`,
      DELETE: (id: number) => `/admin/shop/newsletter-subscriptions/${id}`,
    },
    CONTACT_FORM_SETTINGS: {
      GET: () => `/admin/shop/contact-form-settings`,
      UPDATE: () => `/admin/shop/contact-form-settings`,
    },
    CONTACT_FORM_SUBMISSIONS: {
      GET: (skip: number, take: number) => `/admin/shop/contact-form-submissions?skip=${skip}&take=${take}`,
      DELETE: (id: number) => `/admin/shop/contact-form-submissions/${id}`,
    },
    REDIRECTS: {
      GET: () => `/admin/shop/redirects`,
      CREATE: () => `/admin/shop/redirects`,
      UPDATE: (id: number) => `/admin/shop/redirects/${id}`,
      DELETE: (id: number) => `/admin/shop/redirects/${id}`,
      CHAINS: () => `/admin/shop/redirects/chains`,
      FLATTEN: () => `/admin/shop/redirects/flatten`,
    },
  },
  COURSEGROUP_ATTRIBUTES: {
    GET: (skip: number, take: number, searchPhrase: string = "", orderBy: string = "") =>
      `/admin/coursegroup-attributes?${queryParamsToText([
        ...basePagingToQueryParams(skip, take, searchPhrase),
        { name: "orderBy", value: orderBy },
      ])}`,
    GET_BY_ID: (id: number) => `/admin/coursegroup-attributes/${id}`,
    CREATE: () => `/admin/coursegroup-attributes`,
    UPDATE: (id: number) => `/admin/coursegroup-attributes/${id}`,
    DELETE: (id: number) => `/admin/coursegroup-attributes/${id}`,
    GET_VALUES: (courseGroupId: number) => `/admin/coursegroup-attributes/coursegroups/${courseGroupId}/values`,
    SAVE_VALUES: (courseGroupId: number) => `/admin/coursegroup-attributes/coursegroups/${courseGroupId}/values`,
    BULK_ASSIGN: () => `/admin/coursegroup-attributes/bulk-assign`,
    BULK_UNASSIGN: () => `/admin/coursegroup-attributes/bulk-unassign`,
  },
  STATIC_FILES: {
    GET: () => `/admin/static-files`,
    UPLOAD: () => `/admin/static-files`,
    DELETE: (id: number) => `/admin/static-files/${id}`,
  },
};

export { api, endpoints };

interface QueryParam {
  name: string;
  value?: string;
}

const basePagingToQueryParams = (skip: number, take: number, searchPhrase: string): QueryParam[] => {
  const pars = [
    { name: "skip", value: skip.toString() },
    { name: "take", value: take.toString() },
  ];
  if (searchPhrase) pars.push({ name: "searchPhrase", value: searchPhrase });
  return pars;
};
const queryParamsToText = (queryParams: QueryParam[]): string => {
  return queryParams
    .filter((x) => x.value)
    .map((x) => `${x.name}=${x.value}`)
    .join("&");
};
